/* ==========================================================================
   custom.js - project-specific JS
   Loaded with `defer` from layout/theme.liquid (after main.min.js), so it runs
   once the DOM is parsed. Each feature guards for the elements it needs, so it
   is safe to load site-wide. Add new custom behaviour below.
   ========================================================================== */

(function () {
  'use strict';

  /* --------------------------------------------------------------------------
     Product calculator: bind interactions as soon as the DOM is parsed.

     The product-calculator custom element (assets/product-calculator.min.js)
     binds all of its interaction listeners in onReady(), which its constructor
     defers to the window 'load' event. On a product page 'load' only fires after
     every image/subresource is done, so the width options and length inputs stay
     inert (clicks don't select/recalc) until the whole page has loaded. Run
     onReady() as soon as the element is defined instead, and neutralise the later
     'load' call so nothing double-binds.
     -------------------------------------------------------------------------- */
  function forceCalculatorReady(el) {
    if (el.__calcEarlyInit || typeof el.onReady !== 'function') return;
    el.__calcEarlyInit = true;
    // If the element was defined after 'load', its constructor already ran
    // onReady() (measurementState gets set there) — don't run it again and
    // double-bind listeners.
    if (el.measurementState) return;
    var run = el.onReady.bind(el);
    el.onReady = function () {}; // no-op so the window 'load' handler can't re-bind
    run();
  }

  function initProductCalculator() {
    var els = document.querySelectorAll('product-calculator');
    if (!els.length) return;

    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('product-calculator').then(function () {
        els.forEach(forceCalculatorReady);
      });
    } else {
      els.forEach(forceCalculatorReady);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCalculator, { once: true });
  } else {
    initProductCalculator();
  }

  /* --------------------------------------------------------------------------
     Product calculator: make width selection work on `change` (not just `input`).

     The bundle's handleWidthRadioSelection (wired to the `input` event ONLY) does
     two things that break otherwise: it highlights the chosen chip, and it points
     the real variant <select> (#product-select-option-N) at the chosen width — which
     is what #carpetAddToCart reads to add the correct variant to the cart. WebKit/
     Safari does NOT fire `input` for a radio toggled by clicking a <label> wrapping a
     display:none radio (it fires `change`), so on Safari the width the user picks
     never reaches the cart and the default variant gets added.

     Mirror both on `change` here. The highlight is otherwise handled by custom.css
     (:has(input:checked)); the data-selected write is only for the non-:has fallback.
     Guards make everything idempotent with the bundle when `input` did fire.
     -------------------------------------------------------------------------- */

  // Point the calculator's variant <select> at `value` and let Shopify.OptionSelectors
  // resolve #product-select (the variant #carpetAddToCart adds to the cart).
  function syncWidthSelect(calc, value) {
    if (!calc || !calc.dataset.selectOption || value == null || value === '') return;
    var widthSelect = document.querySelector(calc.dataset.selectOption);
    if (!widthSelect || widthSelect.value === value) return;
    widthSelect.value = value;
    if (widthSelect.value === value) { // only if a matching <option> actually exists
      widthSelect.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  }

  function onWidthChange(e) {
    var radio = e.target;
    if (!radio || radio.type !== 'radio' || !radio.name) return;
    if (radio.name.indexOf('user-width') !== 0 || !radio.checked) return;

    var calc = radio.closest('product-calculator');
    // Scope to the current measurement view so the two width groups don't clear
    // each other's fallback selection.
    var scope = radio.closest('[data-measurement-view]') || calc;
    if (!scope) return;

    // (a) Fallback highlight for browsers without :has() support.
    var selected = scope.querySelectorAll('li > label[data-selected="true"]');
    for (var i = 0; i < selected.length; i++) selected[i].setAttribute('data-selected', 'false');
    var label = radio.closest('label');
    if (label) label.setAttribute('data-selected', 'true');

    // (b) Sync the real variant <select> so the cart uses the chosen width.
    syncWidthSelect(calc, radio.value);
  }

  document.addEventListener('change', onWidthChange);

  // Safety net: if a width was picked before the variant <select> was ready (slow
  // connection), sync from the currently-checked chip in the visible view just before
  // the theme's add-to-cart handler reads the variant. Capture phase runs ahead of
  // the theme's bubble-phase jQuery click handler, and OptionSelectors updates
  // #product-select synchronously, so the read below it sees the correct variant.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#carpetAddToCart') : null;
    if (!btn) return;
    var calc = document.querySelector('product-calculator');
    if (!calc) return;

    var views = calc.querySelectorAll('[data-measurement-view]');
    var checked = null;
    for (var i = 0; i < views.length; i++) {
      if (views[i].offsetParent !== null) { // the visible metric/imperial view
        checked = views[i].querySelector('input[name^="user-width"]:checked');
        if (checked) break;
      }
    }
    if (!checked) checked = calc.querySelector('input[name="user-width-metric"]:checked');
    if (checked) syncWidthSelect(calc, checked.value);
  }, true);

  /* --------------------------------------------------------------------------
     Cart drawer: preserve scroll position across the mobile scroll-lock.

     Opening the cart adds .offCanvasBasket to <html>, which on mobile applies
     html{position:absolute;inset:0;overflow:hidden;height:var(--app-height)}.
     The bundle's toggle (juno.checkToggleBasket) does NO scroll save/restore, so
     that lock collapses the document scroll toward 0; on close the page — and the
     position:fixed header iOS paints against it — can be left at the wrong offset
     (header/promo bars stranded mid-page). Track the last unlocked scroll position
     and restore it when the drawer closes.

     We read a value tracked on scroll (never window.pageYOffset inside the observer)
     because the observer fires after the class is applied, when reading the offset
     would force the just-applied lock to clamp scroll to 0 before we could save it.
     -------------------------------------------------------------------------- */
  (function () {
    var html = document.documentElement;
    var lastY = window.pageYOffset || 0;

    window.addEventListener('scroll', function () {
      // Only trust the offset while the drawer is open-lock-free.
      if (!html.classList.contains('offCanvasBasket')) {
        lastY = window.pageYOffset || 0;
      }
    }, { passive: true });

    var savedY = null;
    var wasOpen = html.classList.contains('offCanvasBasket');
    new MutationObserver(function () {
      var isOpen = html.classList.contains('offCanvasBasket');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) {
        savedY = lastY;              // pre-lock position captured off the scroll handler
      } else if (savedY != null) {
        var y = savedY;
        savedY = null;
        // Restore after the lock (position:absolute) is gone and flow is restored.
        window.requestAnimationFrame(function () {
          window.scrollTo(0, y);
          window.setTimeout(function () { window.scrollTo(0, y); }, 50); // iOS settle backstop
        });
      }
    }).observe(html, { attributes: true, attributeFilter: ['class'] });
  })();
})();

/* ==================================================================
 * Free sample add-to-cart: SKU-based de-duplication (vanilla JS)
 * ------------------------------------------------------------------
 * Takes over the two legacy "Order Sample" click handlers that
 * assets/jquery.js binds to #orderFreeSample (product page) and
 * .orderFreeSample (collection / related grid cards).
 *
 * The same physical swatch is sold under several listings, each with
 * its own sample variant id but the SAME sku, so samples are
 * de-duplicated by sku (falling back to variant id when a sku is
 * missing). This closes these gaps:
 *   1. The same sample added from two different listings (same sku) is
 *      caught and reported instead of slipping through.
 *   2. A "Customers also like" suggestion already in the cart is
 *      skipped silently instead of being added a second time.
 *   3. A duplicate main sample no longer drags its suggestion into the
 *      cart alongside the "already in cart" message.
 *   4. MULTI-colour grid cards are handled here too. Previously we let
 *      them fall through to the legacy jQuery grid handler on the
 *      assumption they were plain links to the PDP -- but that handler
 *      fires on mobile (its gate is a mobile-UA test) once a swatch is
 *      picked, adds straight from the grid deduping by variant id only,
 *      and re-adds the suggestions even when the main sample is a
 *      duplicate. So on a phone the same swatch from a second listing,
 *      and the "already in cart" + suggestion bug, both slipped back in.
 *      We now intercept a multi-colour card as soon as a swatch is
 *      selected (the only case where that handler would add) and route
 *      it through the SKU-dedup path; with no swatch chosen the <a> still
 *      navigates to the PDP, where the same interception applies.
 *   5. An in-flight latch stops a double-tap -- or rapid clicks on two
 *      listings of the same swatch -- from each reading the pre-add cart,
 *      both passing the duplicate check and both POSTing (which Shopify
 *      merges into quantity 2 / two same-sku lines).
 *
 * SKUs come from window.sampleVariantInfo, emitted by the
 * snippets/sample-sku-map.liquid include on product and grid pages, with
 * a static data-sku on the button as a fallback for cards whose inline
 * map <script> never executed (e.g. a grid rebuilt from DOMParser-parsed
 * HTML by the client-side price sort -- parsed <script> nodes are inert).
 *
 * This file is deliberately jQuery-free. It preempts the legacy jQuery
 * handlers with a capture-phase document click listener, so it needs no
 * unbinding and does not depend on script load order.
 * ================================================================== */
(function () {
  'use strict';

  var SAMPLE_LIMIT = 6;

  // Swatches whose add is currently in flight (fetch /cart.js -> fetch
  // /cart/add.js). Marked synchronously at add time and released on every exit,
  // keyed by variant id AND sku, so a second click for the same swatch (double
  // tap, or a second listing of the same physical swatch) is rejected before it
  // can read the stale, pre-add cart and POST a duplicate.
  var pendingVariantIds = {};
  var pendingSkus = {};

  function show(el) {
    if (el) el.style.display = 'block';
  }

  function hide(el) {
    if (el) el.style.display = 'none';
  }

  function infoFor(variantId) {
    return (window.sampleVariantInfo && window.sampleVariantInfo[variantId]) || {};
  }

  // Prefer the live map (it reflects colour-swatch swaps); fall back to the
  // button's static data-sku for cards whose inline map <script> never ran.
  function skuFor(btn, variantId) {
    var mapped = infoFor(variantId).sku;
    if (mapped) return mapped;
    return (btn && btn.getAttribute('data-sku')) || '';
  }

  // "id||id" / "id|id" -> ["id", "id"]
  function parseSuggestionIds(attr) {
    return (attr || '')
      .split('|')
      .map(function (part) { return part.trim(); })
      .filter(Boolean);
  }

  // Restore the button to the label captured before it was switched to "Adding".
  function restoreButtonLabel(btn) {
    var original = btn.getAttribute('data-original-label');
    btn.innerHTML = original !== null ? original : 'Order Sample';
  }

  // Reveal the cart drawer only AFTER ajaxCart has finished rebuilding it, mirroring
  // the carpet add-to-cart handler in snippets/product-bottom.liquid. Opening the
  // drawer synchronously right after ajaxCart.load() drops the grey overlay over a
  // still-loading cart -- the /cart.js fetch is in flight, so it reads as empty
  // ("Spend £199 more" header over a blank drawer). ajaxCart fires
  // 'afterCartLoad.ajaxCart' on document.body once the cart is rebuilt, and
  // 'errorGetCart.ajaxCart' if the fetch fails. These are jQuery custom events
  // (see assets/ajax-cart.js.liquid), so they must be bound with jQuery -- a native
  // addEventListener never sees a jQuery .trigger().
  function revealCartWhenLoaded() {
    var $ = window.jQuery;
    var hasAjaxCart = window.ajaxCart && typeof window.ajaxCart.load === 'function';

    function openDrawer() {
      if (window.juno && window.juno.basketIsRevealed) return; // already open
      var basketToggle = document.getElementById('basketToggle');
      if (basketToggle) basketToggle.click();
    }

    if (!$ || !hasAjaxCart) {
      // No jQuery/ajaxCart event system available: best-effort synchronous fallback.
      if (hasAjaxCart) window.ajaxCart.load();
      openDrawer();
      return;
    }

    $(document.body)
      .off('.sampleAdd')
      .one('afterCartLoad.ajaxCart.sampleAdd', function () {
        $(document.body).off('.sampleAdd');
        openDrawer();
      })
      .one('errorGetCart.ajaxCart.sampleAdd', function () {
        $(document.body).off('.sampleAdd');
        window.location.href = '/cart';
      });

    window.ajaxCart.load();
  }

  function addSample(btn, ctx) {
    // ctx.scope is the element whose message elements we show/hide.
    var q = function (selector) { return ctx.scope.querySelector(selector); };

    // Capture the original label once, before any async work, so it can be
    // restored after the "Adding" state regardless of which exit path is taken.
    if (btn.getAttribute('data-original-label') === null) {
      btn.setAttribute('data-original-label', btn.innerHTML.trim());
    }

    var mainId = btn.getAttribute('data-variant');
    var mainIdNum = Number.parseInt(mainId, 10);
    var mainSku = skuFor(btn, mainId);

    // In-flight latch: mark this swatch as pending BEFORE the async cart read so a
    // near-simultaneous second click (same button double-tap, or a second listing
    // of the same sku) is rejected instead of racing to a duplicate POST. Released
    // on every exit below.
    if (pendingVariantIds[mainIdNum] || (mainSku && pendingSkus[mainSku])) return;
    if (mainIdNum) pendingVariantIds[mainIdNum] = true;
    if (mainSku) pendingSkus[mainSku] = true;
    function releasePending() {
      if (mainIdNum) delete pendingVariantIds[mainIdNum];
      if (mainSku) delete pendingSkus[mainSku];
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var variantIdsInCart = [];
        var skusInCart = [];
        var samplesInCart = 0;

        items.forEach(function (item) {
          variantIdsInCart.push(item.id);
          if (item.product_type === 'Sample') samplesInCart += 1;
          if (item.sku) skusInCart.push(item.sku);
        });

        var inCartByVariant = variantIdsInCart.indexOf(mainIdNum) !== -1;
        var inCartBySku = !!mainSku && skusInCart.indexOf(mainSku) !== -1;

        // Scenario 1 + gap 3: sample already in the cart -> reject, and DO NOT
        // add its "Customers also like" suggestion.
        if (inCartByVariant || inCartBySku) {
          // Scenario 1: the same swatch (same sku) added from a different listing
          // is in the cart under a different name -> override the element's generic
          // message. A plain same-listing re-add keeps the message baked into the element.
          if (inCartBySku && !inCartByVariant) {
            var dupEl = q(ctx.sel.dup);
            if (dupEl) {
              dupEl.textContent = 'This sample is already in the cart.';
            }
          }
          show(q(ctx.sel.dup));
          hide(q(ctx.sel.max6));
          releasePending();
          return;
        }

        // Colour gate -- PDP only. The legacy #orderFreeSample handler shows this
        // error ONLY when the sample is not already in the cart, i.e. AFTER the
        // duplicate check above, not before it. (Checking it first meant a re-add
        // whose swatch selection had been cleared by the post-add cart reload got
        // "please choose your sample colour" instead of the duplicate message.)
        // The legacy grid handler never gated on colour -- grid colour selection
        // is enforced by the navigation branch in the click listener -- so
        // ctx.enforceColour is false for grid cards and this is skipped.
        if (ctx.enforceColour && !ctx.colourChosen) {
          show(q(ctx.sel.colour));
          releasePending();
          return;
        }

        if (samplesInCart >= SAMPLE_LIMIT) {
          show(q(ctx.sel.max6));
          releasePending();
          return;
        }

        hide(q(ctx.sel.colour));
        hide(q(ctx.sel.dup));
        hide(q(ctx.sel.max6));
        btn.innerHTML = 'Adding';

        var lineItems = [{ quantity: 1, id: mainId }];
        var committedIds = variantIdsInCart.slice();
        var committedSkus = skusInCart.slice();
        var running = samplesInCart + 1;
        committedIds.push(mainIdNum);
        if (mainSku) committedSkus.push(mainSku);

        // Scenario 2: add mapped suggestions, silently skipping any already in the
        // cart (by sku or variant id) or that would exceed the sample limit.
        parseSuggestionIds(btn.getAttribute('data-sample-suggestion')).forEach(function (sid) {
          var sidNum = Number.parseInt(sid, 10);
          if (!sidNum || running >= SAMPLE_LIMIT) return;
          if (committedIds.indexOf(sidNum) !== -1) return;
          var suggestionSku = infoFor(sid).sku || '';
          if (suggestionSku && committedSkus.indexOf(suggestionSku) !== -1) return;
          // The value MUST be a string, not a boolean: this theme's ajax-cart
          // buildCart() calls .charAt(0) on every line-item property VALUE (to hide
          // values starting with "_"), so a boolean throws and blanks the drawer.
          // A "_"-prefixed value keeps it hidden; buildCart keys the "Customers also
          // like this" note off the property NAME (_sampleSuggestion), not the value.
          lineItems.push({ quantity: 1, id: sid, properties: { _sampleSuggestion: '_' } });
          committedIds.push(sidNum);
          if (suggestionSku) committedSkus.push(suggestionSku);
          running += 1;
        });

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lineItems })
        })
          .then(function (response) {
            // fetch only rejects on a network error, so a 4xx/5xx (e.g. a sample or
            // suggestion variant that is unavailable -> 422) still resolves. Treat a
            // non-OK response as a failure so we never reveal an empty cart drawer.
            if (!response.ok) throw new Error('cart/add ' + response.status);
            return response.json();
          })
          .then(function () {
            document.querySelectorAll('.js-cart-menu-links').forEach(function (el) {
              el.classList.add('hide');
            });

            show(document.getElementById('freeSampleAddedMessage'));
            if (!ctx.isPdp) show(q('.freeSampleAddedMessage'));

            var goToCheckout = document.getElementById('GoToCheckout');
            if (goToCheckout) goToCheckout.classList.add('active');

            restoreButtonLabel(btn);
            releasePending();

            revealCartWhenLoaded();
          })
          .catch(function () {
            restoreButtonLabel(btn);
            show(document.getElementById('responseFailure'));
            releasePending();
          });
      })
      .catch(function () {
        restoreButtonLabel(btn);
        releasePending();
      });
  }

  // Capture-phase interception runs before the legacy jQuery click handlers on
  // the target, so stopImmediatePropagation() prevents them from also firing.
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('#orderFreeSample, .orderFreeSample');
      if (!btn) return;

      var isPdp = btn.id === 'orderFreeSample';

      if (btn.classList.contains('disabled')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      var card = isPdp ? null : btn.closest('.item');

      // Grid cards: single-colour cards add straight from the grid; multi-colour
      // cards add once a swatch is picked, otherwise the <a> navigates to the PDP
      // (where the same interception applies). We MUST intercept the swatch-picked
      // case here -- it is exactly when the legacy jQuery grid handler would add
      // on mobile, deduping by variant id only and re-adding suggestions on a
      // duplicate. With no swatch chosen we let the click navigate.
      if (!isPdp && !btn.classList.contains('isSingleProduct')) {
        var swatchSelected =
          !!card && card.querySelectorAll('.swatch-colours .list li.selected').length > 0;
        if (!swatchSelected) {
          // No colour picked yet: let the <a> navigate to the PDP (do NOT
          // preventDefault), but still block the legacy jQuery grid handler.
          // Unlike the online-carpets bundle, this theme's grid handler has no
          // "swatch selected" guard, so on mobile (its gate is a mobile-UA test)
          // it would otherwise add the DEFAULT colour's sample straight from the
          // grid. stopImmediatePropagation keeps navigation the only outcome.
          e.stopImmediatePropagation();
          return;
        }
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      var ctx;
      if (isPdp) {
        var pdpHasSwatches = !!document.querySelector('.swatch-colours .grid');
        ctx = {
          isPdp: true,
          scope: document,
          enforceColour: true,
          colourChosen:
            !pdpHasSwatches ||
            document.querySelectorAll('.swatch-colours .grid li.selected').length > 0,
          sel: {
            dup: '#duplicateSampleError',
            max6: '#max6SampleError',
            colour: '#addASampleColourError'
          }
        };
      } else {
        // The legacy grid handler never gated on colour selection (it only ever
        // hides .addASampleColourError, never shows it); a multi-colour card with
        // no swatch chosen is already handled by the navigation branch above. So
        // the grid path does not enforce colour.
        ctx = {
          isPdp: false,
          scope: card || document,
          enforceColour: false,
          sel: {
            dup: '.duplicateSampleError',
            max6: '.max6SampleError',
            colour: '.addASampleColourError'
          }
        };
      }

      addSample(btn, ctx);
    },
    true
  );
})();

/* --------------------------------------------------------------------------
   Product photo lightbox (Featherlight gallery): instant slide changes.

   FeatherlightGallery.navigateTo() cross-fades between photos by dimming the
   current image to 0.2 opacity (galleryFadeOut) and then fading the next one
   back in, which reads as a brief "loading" flicker every time you press the
   prev/next arrow. Swap the content in place with no dim instead -- the
   outgoing image stays fully opaque until the next one is ready. Every other
   behaviour is preserved: beforeContent() still toggles the first/last-slide
   classes and getContent()/setContent()/afterContent() still run, so captions
   and the `loaded` class keep working.

   jquery.js (which defines $.featherlightGallery) is a synchronous <script> in
   the body, so it has already run by the time this deferred file executes; the
   gallery instances are created later on ready, so they pick up this override.
   -------------------------------------------------------------------------- */
(function () {
  var $ = window.jQuery;
  if (!$ || !$.featherlightGallery) return;

  $.featherlightGallery.prototype.navigateTo = function (target) {
    var self = this,
      slides = self.slides(),
      count = slides.length;

    target = ((target % count) + count) % count;
    self.$currentTarget = slides.eq(target);
    self.beforeContent();

    return $.when(self.getContent()).always(function (content) {
      self.setContent(content);
      self.afterContent();
    });
  };
})();
